import { useState, useEffect } from 'react';
import { Key, useTenantPermissions } from '@maxtropy/components';
import { AreaItem, fetchAreaJson } from '../api/electricity';
import { lazyHolderSingletonGetter, SubjectHolder } from '@maxtropy/components/es/lib/SubjectHolder';
import { isNil } from 'lodash-es';

export const useQuery = <T>(action: () => Promise<T | undefined>) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<T | undefined>(undefined);
  useEffect(() => {
    setIsLoading(true);
    action()
      .then(setData)
      .finally(() => setIsLoading(false));
  }, [action]);
  return { isLoading, data };
};

export function useHasPermission(key: string) {
  const [status, setStatus] = useState<boolean>();
  const permissions = useTenantPermissions();
  useEffect(() => {
    setStatus((permissions ?? []).findIndex(i => i === key) !== -1);
  }, [permissions, key]);
  return status;
}

export const formatDistrictData = (data?: AreaItem[], filterKey?: string): AreaItem[] => {
  if (!data) return [];
  return data
    .filter(item => item.adcode !== filterKey)
    .map(item => ({
      ...item,
      districts: item.districts ? formatDistrictData(item.districts, item.adcode) : undefined,
    }));
};

export const getParents = (nodes: AreaItem[], key: string): AreaItem[] => {
  const res = [];
  for (let node of nodes ?? []) {
    if (node.adcode === key) {
      res.unshift(node);
      break;
    } else {
      const children = getParents(node.districts ?? [], key);
      if (children && children.length) {
        res.unshift(node, ...children);
        break;
      }
    }
  }
  return res;
};

export const getAreaJson = /*#__PURE__*/ lazyHolderSingletonGetter(
  () => new SubjectHolder(() => fetchAreaJson().then(res => formatDistrictData(res?.districts)))
);

export function useAreaJson() {
  const currentInstance = getAreaJson();
  const [state, setState] = useState(currentInstance.current());

  useEffect(() => {
    const subcription = currentInstance.subscribe(setState);
    return () => {
      subcription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

export function useAdcodeToName(adcode?: string) {
  const areaJson = useAreaJson();
  const [name, setName] = useState<string>('');
  useEffect(() => {
    if (areaJson && adcode) {
      setName(
        getParents(areaJson, adcode)
          .map(i => i.name)
          .join('/')
      );
    }
  }, [adcode, areaJson]);
  return name;
}

// 取整方法
export const keepZeroOrLine = (data?: number) => {
  if (!isNil(data)) {
    return data.toFixed(0);
  } else {
    return '--';
  }
};

export const keepFeeZeroOrLine = (data?: number) => {
  if (!isNil(data)) {
    return Number(data.toFixed(0)).toLocaleString();
  } else {
    return '--';
  }
};
export const keepFeeZeroAndWanOrLine = (data?: number) => {
  if (!isNil(data)) {
    if (data >= 100000) {
      // 除以 10000，保留 0 位小数，去掉多余的 .00
      return (data / 10000).toFixed(0).replace(/\.00$/, '') + '万';
    }
    return Number(data.toFixed(0)).toLocaleString();
  } else {
    return '--';
  }
};

export const keepOneOrLine = (data?: number) => {
  if (!isNil(data)) {
    return data.toFixed(1);
  } else {
    return '--';
  }
};

export const formatOneDecimalOrDash = (
  value?: number | string | null,
  fixed: number = 1,
  emptyPlaceholder: string = '--'
): string => {
  if (value === null || value === undefined) {
    return emptyPlaceholder;
  }

  const num = typeof value === 'string' ? Number(value) : value;

  if (Number.isNaN(num)) {
    return emptyPlaceholder;
  }

  return num.toFixed(fixed);
};

export const keepOneAndWanOrLine = (data?: number) => {
  if (!isNil(data)) {
    if (data >= 10000) {
      // 除以 10000，保留 2 位小数，去掉多余的 .00
      return (data / 10000).toFixed(1).replace(/\.00$/, '') + '万';
    }
    return data.toFixed(1);
  } else {
    return '--';
  }
};
export const keepTwoAndWanOrLine = (data?: number) => {
  if (!isNil(data)) {
    if (data >= 10000) {
      // 除以 10000，保留 2 位小数，去掉多余的 .00
      return (data / 10000).toFixed(2).replace(/\.0+$/, '') + '万';
    }
    return data.toFixed(2);
  } else {
    return '--';
  }
};
export const keepOneAndWan = (data?: number): { value: string; unit: string } => {
  if (!isNil(data)) {
    if (data >= 10000) {
      // 除以 10000，保留 2 位小数，去掉多余的 .00
      return {
        value: (data / 10000).toFixed(1).replace(/\.00$/, ''),
        unit: '万',
      };
    }
    return {
      value: data.toFixed(1),
      unit: '',
    };
  } else {
    return {
      value: '--',
      unit: '',
    };
  }
};

export const keepTwoOrLine = (data?: number | null) => {
  if (!isNil(data)) {
    return data.toFixed(2);
  } else {
    return '--';
  }
};

/**
 * 格式化设备利用率，四舍五入保留小数点后3位
 * 若数值不足0.001%，则显示为0.001%（0除外）
 */
export const formatDeviceUtilization = (data?: number): string => {
  if (data == null) {
    return '--';
  }

  // 如果是0，则直接返回0
  if (data === 0) {
    return '0.000';
  }

  // 四舍五入保留三位小数
  const rounded = parseFloat(data.toFixed(3));

  // 如果四舍五入后的值小于0.001且不为0，则显示0.001
  if (rounded < 0.001 && rounded > 0) {
    return '0.001';
  }

  // 否则返回四舍五入的结果，确保有三位小数
  return rounded.toFixed(3);
};

export const formatNumberWithUnit = (data?: number, unit?: string) => {
  if (isNil(data)) return '--';
  if (!unit) return;
  const convertToWanUnits = ['kWh', 'm³', 't'];
  if (convertToWanUnits.includes(unit)) {
    if (data >= 10000) {
      return `${(data / 10000).toFixed(2)}万${unit}`;
    }
  }
  return `${data.toFixed(2)}${unit}`;
};

export const keepTwoWithNumberOrLine = (data?: number) => {
  if (!isNil(data)) {
    return Number(data.toFixed(2));
  } else {
    return '--';
  }
};
export const keepTwoOrNull = (data?: number) => {
  if (!isNil(data)) {
    return Number(data.toFixed(2));
  } else {
    return null;
  }
};

export const keepTwoOrEmpty = (data?: number) => {
  if (!isNil(data)) {
    return data.toFixed(2);
  } else {
    return '';
  }
};

export const keepOneNull = (data?: number) => {
  if (!isNil(data)) {
    return data.toFixed(1);
  } else {
    return '--';
  }
};

export const keepTwoNull = (data?: number) => {
  if (!isNil(data)) {
    return data.toFixed(2);
  } else {
    return '--';
  }
};
export const keepFourOrNull = (data?: number) => {
  if (!isNil(data)) {
    return data.toFixed(4);
  } else {
    return null;
  }
};
export const keepFourOrLine = (data?: number) => {
  if (!isNil(data)) {
    return data.toFixed(4);
  }
};

export const keepZeroPercentOrLine = (data?: number): string => {
  if (!isNil(data)) {
    return (data * 100).toFixed(0);
  } else {
    return '--';
  }
};

export const keepZeroPercentNoNaN = (data?: number): string => {
  if (typeof data !== 'number' || !Number.isFinite(data)) {
    return '--';
  }
  return (data * 100).toFixed(0);
};

export const keepOnePercentOrLine = (data?: number): string => {
  if (!isNil(data)) {
    return (data * 100).toFixed(1);
  } else {
    return '--';
  }
};

export const keepPercentOrLine = (data?: number): string => {
  if (!isNil(data)) {
    return (data * 100).toFixed(2);
  } else {
    return '--';
  }
};

export function formatPercentWithoutSign(rate?: number | null): string {
  if (rate === null || rate === undefined) return '--';
  const val = Math.abs(rate);
  return keepZeroPercentOrLine(val);
}

export const keepPercentTwoUndfined = (data?: number): number | undefined => {
  if (!isNil(data)) {
    return Number((data * 100).toFixed(2));
  } else {
    return undefined;
  }
};

export const keepPercentOneUndfined = (data?: number): number | undefined => {
  if (!isNil(data)) {
    return Number((data * 100).toFixed(1));
  } else {
    return undefined;
  }
};

export const keepPercentTwoNull = (data?: number): number | null => {
  if (!isNil(data)) {
    return Number((data * 100).toFixed(2));
  } else {
    return null;
  }
};

export const keepPercentOneNull = (data?: number): number | null => {
  if (!isNil(data)) {
    return Number((data * 100).toFixed(1));
  } else {
    return null;
  }
};

export const keepWithNumberOrNull = (data?: number, fixed: number = 1): number | null => {
  if (!isNil(data)) {
    return Number(data.toFixed(fixed));
  } else {
    return null;
  }
};

export const keepTwoOrNullByKey = (data?: Key, fixed: number = 1): number | null => {
  if (!isNil(data)) {
    if (typeof data === 'string') {
      return Number(Number(data).toFixed(fixed));
    }
    return Number(data.toFixed(2));
  } else {
    return null;
  }
};

export function formatPowerKWh(value?: number): string {
  if (value == null) return '--';
  if (value >= 100000) {
    return (value / 10000).toFixed(1) + '万';
  }
  return Math.round(value).toString();
}
export function formatPowerKWhAndUnit(value?: number): { value: string; unit: string } {
  if (value == null)
    return {
      value: '--',
      unit: '',
    };
  if (value >= 100000) {
    return {
      value: (value / 10000).toFixed(1),
      unit: '万',
    };
  }
  return {
    value: Math.round(value).toString(),
    unit: '',
  };
}

/**
 * 单位转换（千 ->万）
 * @param value 数值
 * @param fixed 保留小数位（默认 1）
 * @returns 格式化后的字符串
 */
export function formatThousandUnit(value?: Key, fixed: number = 1): string {
  if (value == null) return '--';
  if (typeof value === 'string') {
    value = Number(value);
  }

  if (fixed == null) fixed = 1;

  const absVal = Math.abs(value);

  if (absVal >= 10000) {
    return (value / 10000).toFixed(fixed) + ' 万';
  }
  return value.toFixed(fixed);
}
export function formatThousandUnitDefaultTwo(value?: Key, fixed: number = 2): string {
  if (value == null) return '--';
  if (typeof value === 'string') {
    value = Number(value);
  }

  if (fixed == null) fixed = 2;

  const absVal = Math.abs(value);

  if (absVal >= 10000) {
    return (value / 10000).toFixed(fixed) + ' 万';
  }
  return value.toFixed(fixed);
}
export function formatThousandAndUnit(
  value?: Key,
  fixed: number = 1,
  isInterger: boolean = false
): { value: string; unit: string } {
  if (value == null)
    return {
      value: '--',
      unit: '',
    };
  if (typeof value === 'string') {
    value = Number(value);
  }
  let absVal = Math.abs(value);
  if (isInterger) {
    absVal = Number(Math.abs(value).toFixed(0));
  }

  if (absVal >= 10000) {
    return {
      value: (value / 10000).toFixed(fixed),
      unit: '万',
    };
  }
  return {
    value: value.toFixed(isInterger ? 0 : fixed),
    unit: '',
  };
}

/**
 * 单位转换（kg->t）
 * @param value 数值
 * @param fixed 保留小数位（默认 2）
 * @returns 格式化后的字符串
 */
export function formatKgToTonUnit(value?: Key, fixed: number = 1): { value: string; unit: string } {
  if (value == null)
    return {
      value: '--',
      unit: '',
    };
  if (typeof value === 'string') {
    value = Number(value);
  }

  const absVal = Math.abs(value);

  if (absVal >= 1000) {
    return {
      value: (value / 1000).toFixed(fixed),
      unit: 't',
    };
  }
  return {
    value: value.toFixed(fixed),
    unit: 'kg',
  };
}

export function getAssetBaseUrl() {
  if (window.__MICRO_APP_ENVIRONMENT__) {
    return '/ms-dc'; // 注意这里必须确保始终是以 / 开头
  }
  return process.env.PUBLIC_URL;
}
